import { GetServerSideProps } from 'next';

import { PageDetail } from '@components/pageDetail/PageDetail';
import { Seo } from '@components/seo/Seo';

import { isProduction } from '@common/utils/environment';
import {
	getEventsCollections,
	serverSideWithSiteData,
} from '@common/utils/page';
import { queryFromDato } from '@data/datocms/client';
import { homePageQuery } from '@data/datocms/queries';
import { supabaseAdminClient } from '@data/supabase/admin';
import { getEventList } from '@data/supabase/getEventList';
import { PageResponse, SiteData, SupabaseEventRecord } from '@interfaces';

interface HomePageProps extends PageResponse {
	justAnnouncedEvents: SupabaseEventRecord[];
}

const HomePage = ({
	page,
	programme,
	justAnnouncedEvents,
	site,
}: HomePageProps & SiteData) => {
	return (
		<>
			<Seo metaTagData={[...page.seoMetaTags, ...site.faviconMetaTags]} />
			<PageDetail
				{...page}
				isHomePage
				justAnnouncedEvents={justAnnouncedEvents}
				programmeLabels={programme}
			/>
		</>
	);
};

// This uses server side rendering in preparation for using pageQuery and shareable previews
export const getServerSideProps: GetServerSideProps =
	serverSideWithSiteData<HomePageProps>(async ({ res }) => {
		const homePageResponse = await queryFromDato<PageResponse>({
			query: homePageQuery,
		});

		const page = homePageResponse?.page;
		const programme = homePageResponse?.programme;

		if (!page || !programme) {
			return {
				notFound: true,
			};
		}

		// WARN: this changes the page in place
		await getEventsCollections(page, supabaseAdminClient);

		if (isProduction()) {
			res.setHeader(
				'Cache-Control',
				'public, s-maxage=300, stale-while-revalidate=60',
			);
		}

		const { events: justAnnouncedEvents } = await getEventList({
			options: {
				elsewherePresents: false,
				excludeDice: true,
				justAnnounced: true,
			},
			supabaseClient: supabaseAdminClient,
		});

		return {
			props: {
				page,
				programme,
				justAnnouncedEvents,
			},
		};
	});

export default HomePage;
